// apps/web/src/pages/tracer-page.tsx — Fase 01, Plano 39 (NFR-08/NFR-09).
//
// Tracer executável browser -> API -> Redis/BullMQ: consome GET /health/ready
// (apps/api/src/modules/health/health.controller.ts, Plano 01-02) via fetch
// real, com `credentials: 'include'` (sessão futura será cookie HttpOnly,
// ADR-003 — nunca Web Storage) e um `x-request-id` gerado no cliente, para
// que o mesmo id apareça nos dois lados (rastreabilidade ponta a ponta).
//
// Estado "up"/"down" nunca reproduz texto bruto de erro (fetch reject, JSON
// malformado, stack) — só o corpo JÁ sanitizado que o próprio controller
// devolve (`sanitizeChecks`, health.controller.ts) ou uma mensagem genérica
// fixa quando nem isso está disponível (rede fora do ar).
//
// GAP HERDADO (não introduzido por este plano): GET /health/ready hoje só
// reporta os indicadores 'app' e 'queue' (Redis/BullMQ) — Postgres ainda não
// tem indicador registrado (01-02-SUMMARY.md § Issues Encountered #1,
// WINDOWS.md id 3). Este tracer prova browser->API->Redis/BullMQ de ponta a
// ponta; a perna Postgres fica pendente de um plano futuro que registre
// `PostgresHealthIndicator` via `HealthService.registerIndicator()`.
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

const REQUEST_ID_HEADER = 'x-request-id';
// `import.meta.env` tipa entradas VITE_* como `any` (vite/client) — cast
// explícito para satisfazer o lint tipado (@typescript-eslint/no-unsafe-assignment),
// não porque o valor real seja incerto (é sempre string ou undefined em build/dev).
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

interface HealthCheckResult {
  readonly status: 'up' | 'down';
}

interface ReadyBody {
  readonly status: 'ok' | 'error';
  readonly checks: Record<string, HealthCheckResult>;
  readonly requestId?: string;
}

type TracerState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly body: ReadyBody }
  | { readonly kind: 'unavailable'; readonly requestId?: string };

function newRequestId(): string {
  return crypto.randomUUID();
}

async function fetchReadiness(): Promise<TracerState> {
  const requestId = newRequestId();
  try {
    const response = await fetch(`${API_BASE_URL}/health/ready`, {
      method: 'GET',
      credentials: 'include',
      headers: { [REQUEST_ID_HEADER]: requestId },
    });
    // 200 e 503 sempre têm corpo JSON estruturado — mas um corpo malformado
    // (proxy quebrado, resposta não-JSON) não pode virar mensagem crua na UI.
    let body: ReadyBody;
    try {
      body = (await response.json()) as ReadyBody;
    } catch {
      return { kind: 'unavailable', requestId };
    }
    if (response.ok) {
      return { kind: 'ready', body };
    }
    return { kind: 'unavailable', requestId: body.requestId ?? requestId };
  } catch {
    // Falha de rede (API fora do ar, DNS, timeout): nunca expor a mensagem
    // bruta do erro (pode conter host/porta) no DOM.
    return { kind: 'unavailable', requestId };
  }
}

export function TracerPage(): ReactElement {
  const [state, setState] = useState<TracerState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchReadiness()
      .then((result) => {
        if (!cancelled) {
          setState(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ kind: 'unavailable' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <main aria-busy="true">
        <p>Verificando disponibilidade do sistema…</p>
      </main>
    );
  }

  if (state.kind === 'ready') {
    return (
      <main>
        <h1>Sistema pronto</h1>
        <ul>
          {Object.entries(state.body.checks).map(([name, check]) => (
            <li key={name}>
              {name}: {check.status}
            </li>
          ))}
        </ul>
      </main>
    );
  }

  return (
    <main role="alert">
      <h1>Sistema indisponível</h1>
      <p>Não foi possível confirmar a disponibilidade do sistema agora. Tente novamente em instantes.</p>
      {state.requestId ? <p>Identificador da requisição: {state.requestId}</p> : null}
    </main>
  );
}
